export class FileParser {
    static async parseFiles(files) {
        let extractedTexts = [];
        for (const file of files) {
            let text = '';
            const ext = file.name.split('.').pop().toLowerCase();

            try {
                if (ext === 'pdf') {
                    text = await this.parsePDF(file);
                } else if (ext === 'docx') {
                    text = await this.parseDOCX(file);
                } else if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
                    text = await this.parseExcel(file);
                } else if (ext === 'txt' || ext === 'md') {
                    text = await file.text();
                } else if (ext === 'hwpx') {
                    text = await this.parseHWPX(file);
                } else if (ext === 'hwp') {
                    text = await this.parseHWP(file);
                } else if (['mp3', 'wav', 'm4a', 'ogg', 'webm'].includes(ext)) {
                    text = await this.parseAudio(file);
                } else if (['png', 'jpg', 'jpeg', 'webp', 'heic', 'gif'].includes(ext)) {
                    text = await this.parseImage(file);
                } else {
                    const msg = `지원하지 않는 파일 형식입니다: .${ext}\n(지원: PDF, DOCX, XLSX, HWP, HWPX, TXT, MD, MP3, WAV, M4A, 이미지(PNG, JPG, WEBP, GIF, HEIC))`;
                    alert(msg);
                    console.warn(msg);
                    continue;
                }
                
                if (text) {
                    extractedTexts.push(`[Source: ${file.name}]\n${text.trim()}`);
                }
            } catch (error) {
                console.error(`Error parsing ${file.name}:`, error);
                extractedTexts.push(`[Source: ${file.name}] (Parsing failed)`);
            }
        }
        return extractedTexts.join('\n\n====\n\n');
    }

    static async parsePDF(file) {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({data: arrayBuffer}).promise;
        let text = '';
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            text += content.items.map(item => item.str).join(' ') + '\n';
        }
        return text;
    }

    static async parseDOCX(file) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({arrayBuffer: arrayBuffer});
        return result.value;
    }

    static async parseExcel(file) {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        let text = '';
        workbook.SheetNames.forEach(sheetName => {
            const sheet = workbook.Sheets[sheetName];
            text += `Sheet: ${sheetName}\n`;
            text += XLSX.utils.sheet_to_csv(sheet) + '\n';
        });
        return text;
    }

    static async parseHWPX(file) {
        const zip = new JSZip();
        const loadedZip = await zip.loadAsync(file);
        let text = '';
        
        for (const [filename, fileData] of Object.entries(loadedZip.files)) {
            if (filename.startsWith('Contents/section') && filename.endsWith('.xml')) {
                const xmlContent = await fileData.async('text');
                // Remove XML tags to extract text
                const extracted = xmlContent.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                text += extracted + '\n';
            }
        }
        return text;
    }

    static async parseHWP(file) {
        try {
            // Import and initialize @rhwp/core WASM Core Parser
            const { default: init, HwpDocument } = await import('@rhwp/core');
            try {
                await init({ module_or_path: '/rhwp_bg.wasm' });
            } catch (initErr) {
                // Bypass if already initialized
            }

            const arrayBuffer = await file.arrayBuffer();
            const uint8 = new Uint8Array(arrayBuffer);
            const doc = new HwpDocument(uint8);
            
            const pageCount = doc.pageCount();
            let allText = '';
            
            for (let i = 0; i < pageCount; i++) {
                const layoutJsonStr = doc.getPageTextLayout(i);
                if (layoutJsonStr) {
                    const layout = JSON.parse(layoutJsonStr);
                    const pageText = this._extractTextFromLayout(layout);
                    if (pageText) {
                        allText += pageText + '\n';
                    }
                }
            }
            
            doc.free(); // Free WASM core instance memory
            
            if (allText) {
                const cleanText = allText.replace(/\s+/g, ' ').trim();
                return cleanText;
            }
            
            return "⚠️ HWP 텍스트 추출 결과가 없습니다.";
        } catch (e) {
            console.warn("WASM HWP Parser failed, falling back to legacy OLE parsing:", e);
            return this.parseHWPLegacy(file);
        }
    }

    static async parseHWPLegacy(file) {
        const arrayBuffer = await file.arrayBuffer();
        const cfb = CFB.read(new Uint8Array(arrayBuffer), { type: 'array' });
        
        let text = '';
        cfb.FullPaths.forEach((path, i) => {
            if (path.includes('BodyText')) {
                const fileNode = cfb.FileIndex[i];
                if (fileNode && fileNode.content) {
                    const decoder = new TextDecoder('utf-16le', { fatal: false });
                    const decoded = decoder.decode(new Uint8Array(fileNode.content));
                    text += decoded + '\n';
                }
            }
        });

        if (!text) {
            cfb.FullPaths.forEach((path, i) => {
                if (path.includes('PrvText')) {
                    const fileNode = cfb.FileIndex[i];
                    if (fileNode && fileNode.content) {
                        const decoder = new TextDecoder('utf-16le', { fatal: false });
                        text += decoder.decode(new Uint8Array(fileNode.content)) + '\n';
                    }
                }
            });
        }

        const safeRegex = /[가-힣a-zA-Z0-9]{1,15}[가-힣a-zA-Z0-9\s.,?!()\-:;'"\[\]]{0,100}/g;
        const matches = text.match(safeRegex);
        
        if (matches) {
            const filteredText = matches.join(' ').replace(/\s+/g, ' ').trim();
            if (filteredText.length < 20) {
                return "⚠️ HWP 파일에서 유효한 텍스트를 추출하지 못했습니다 (바이너리 데이터만 감지됨).";
            }
            return filteredText;
        }
        return "⚠️ HWP 텍스트 추출 결과가 없습니다 (또는 데이터가 손상되었습니다).";
    }

    static _extractTextFromLayout(obj) {
        if (!obj) return '';
        let text = '';
        if (typeof obj === 'string') return obj;
        if (Array.isArray(obj)) {
            return obj.map(item => this._extractTextFromLayout(item)).join(' ');
        }
        if (typeof obj === 'object') {
            if (typeof obj.text === 'string') {
                text += obj.text + ' ';
            }
            if (Array.isArray(obj.runs)) {
                text += obj.runs.map(r => typeof r === 'object' && r.text ? r.text : '').join(' ');
            }
            for (const key in obj) {
                if (key !== 'text' && key !== 'runs') {
                    text += this._extractTextFromLayout(obj[key]) + ' ';
                }
            }
        }
        return text;
    }

    static async parseAudio(file) {
        try {
            const { aiService } = await import('./aiService.js');
            return await aiService.transcribeAudio(file);
        } catch (err) {
            console.error("Audio transcription failed:", err);
            return `⚠️ 음성 인식 실패 (${file.name}): ${err.message}`;
        }
    }

    static async parseImage(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                resolve(`[📸 첨부 사진 정보: ${file.name} (${Math.round(file.size / 1024)}KB) - 이 이미지가 본문에 첨부되었으므로, 해당 사진에 묘사된 내용(풍경, 음식 비주얼, 상품 외관 등)을 자연스럽게 묘사하고 필요한 경우 본문 적절한 위치에 [사진: ${file.name}] 형태로 이미지 삽입 가이드를 기재해주세요.]`);
            };
            reader.onerror = (err) => reject(err);
            reader.readAsDataURL(file);
        });
    }
}
